import React, { useState } from 'react';
import { Popover, Button, Card, Typography } from 'antd';
import { CloseOutlined, GlobalOutlined, MobileOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import { QRCodeSVG } from 'qrcode.react';
import GooglePlay from '@images/google-play-badge.png';
import AppleAppStore from '@images/apple-app-store-badge.png';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

const { Title, Text } = Typography;

const ExerciseButton = styled(Button)`
  display: flex;
  align-items: center;
  padding: 4px 12px !important;
  border-radius: 8px;
  border-color: var(--productCoral) !important;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  background: var(--productCoral) !important;
`;

const AppButton = ({
  url,
  src,
  alt,
  'data-testid': testId
}: {
  url: string;
  src: string;
  alt: string;
  'data-testid'?: string;
}) => {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      css={css`
        display: block;
        width: 140px;
        max-width: 100%;
        transition: opacity 0.2s;
        &:active {
          opacity: 0.7;
        }
      `}
    >
      <img
        src={src}
        alt={alt}
        css={css`
          width: 100%;
          height: auto;
          display: block;
        `}
      />
    </a>
  );
};

const ExercisePlan = ({ url }: { url: string }) => {
  const { t } = useTranslation();
  const isMobileView = 'ontouchstart' in document.documentElement;

  const [open, setOpen] = useState(false);

  const modalContent = (
    <Card
      style={{
        width: '100%',
        maxWidth: isMobileView ? 'calc(100vw - 56px)' : 420,
        boxShadow: 'none',
        border: 'none',
        boxSizing: 'border-box'
      }}
      styles={{ body: { padding: isMobileView ? 8 : 16 } }}
    >
      <div style={{ position: 'relative', textAlign: 'center', marginBottom: 24 }}>
        <Title level={4} style={{ margin: '0 0 8px 0', fontWeight: 700 }}>
          {t('Access Exercise Plan')}
        </Title>
        <Text type="secondary">{t("Choose how you'd like to continue")}</Text>
        <CloseOutlined
          style={{
            position: 'absolute',
            right: 0,
            top: 4,
            cursor: 'pointer',
            fontSize: 16,
            color: 'var(--lightProductGray)',
            padding: 4
          }}
          onClick={() => setOpen(false)}
        />
      </div>

      <div
        style={{
          border: '1.5px solid var(--productIron)',
          borderRadius: 12,
          padding: isMobileView ? 16 : 24,
          marginBottom: 16
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              backgroundColor: '#f3e8ff',
              color: '#9333ea',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: 16,
              fontSize: 24,
              flexShrink: 0
            }}
          >
            <MobileOutlined />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{t('Download App')}</div>
            {!isMobileView && (
              <Text type="secondary" style={{ fontSize: 14 }}>
                {t('Scan the QR code to download our mobile app')}
              </Text>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          {!isMobileView && (
            <>
              <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                <div style={{ height: 110, width: 110 }}>
                  <QRCodeSVG
                    value={url}
                    css={css`
                      height: 100%;
                      width: 100%;
                    `}
                  />
                </div>
              </div>

              <div
                style={{
                  width: 1,
                  height: 110,
                  backgroundColor: 'var(--productIron)',
                  margin: '0 8px'
                }}
              />
            </>
          )}

          <div
            style={{
              flex: isMobileView ? 'none' : 1,
              width: isMobileView ? '100%' : 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <AppButton
              url={url}
              src={AppleAppStore.src}
              alt={t('Download on the App Store')}
              data-testid="app-store-button"
            />
            <AppButton
              url={url}
              src={GooglePlay.src}
              alt={t('Get it on Google Play')}
              data-testid="google-play-button"
            />
          </div>
        </div>
      </div>

      <div
        style={{
          border: '1.5px solid var(--productIron)',
          borderRadius: 12,
          padding: isMobileView ? 16 : 24
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              backgroundColor: '#e0f2fe',
              color: '#2563eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: 16,
              fontSize: 24,
              flexShrink: 0
            }}
          >
            <GlobalOutlined />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{t('Browser Version')}</div>
            <Text type="secondary" style={{ fontSize: 14 }}>
              {t('Continue with the web version')}
            </Text>
          </div>
        </div>

        <Button
          block
          href={url}
          target="_blank"
          type="primary"
          style={{
            height: 48,
            borderRadius: 8,
            fontWeight: 600,
            fontSize: 16
          }}
          data-testid="open-in-browser-button"
        >
          {t('Open in Browser')}
        </Button>
      </div>
    </Card>
  );

  return (
    <Popover
      content={modalContent}
      placement="topLeft"
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      showArrow={false}
      arrow={false}
      styles={{
        body: {
          borderRadius: 12,
          padding: isMobileView ? 12 : 24,
          boxShadow: '0 3px 6px #00000029',
          boxSizing: 'border-box'
        },
        root: {
          maxWidth: 'calc(100vw - 32px)'
        }
      }}
    >
      <ExerciseButton type="link" data-testid="exercise-plan-button">
        {t('My Exercise Plan')}
      </ExerciseButton>
    </Popover>
  );
};

export default ExercisePlan;
